import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import { getExactDiffFromCurrentTimeDayJs } from 'src/consumer/helpers/utilFunctions'

dayjs.extend(utc)

export function buildSlotDurationForFullCalendarFromEventTypeDuration(durationInMins) {
  const durationInHours = durationInMins / 60

  return `00:${durationInMins}:00`
}

export function getNextVisitDateDifference(nextVisitDate, bookingDate) {
  const bookingDateClone = bookingDate.clone()
  const nextVisitDateDiff = getExactDiffFromCurrentTimeDayJs(nextVisitDate, bookingDateClone, false)
  if (nextVisitDateDiff === 'Today') {
    return 'The same day'
  }
  if (nextVisitDateDiff === 'Tomorrow') {
    return 'The next day'
  }
  return "in " + nextVisitDateDiff
}

/**
 * Get clinic timezone with fallback to IST (+05:30)
 * @param {Object} clinicLocation - Clinic location object
 * @returns {string} Timezone offset string (e.g., '+05:30')
 */
export function getClinicTimezone(clinicLocation) {
  if (!clinicLocation) {
    return '+05:30' // Default to IST
  }

  // Try to get timezone from schedules
  let timezone = clinicLocation?.schedules?.[0]?.timezone
  if (timezone) {
    // Decode if URL encoded
    if (typeof timezone === 'string' && timezone.includes('%')) {
      try {
        timezone = decodeURIComponent(timezone)
      } catch {
        timezone = '+05:30'
      }
    }
    return timezone
  }

  // Try to get timezone from timeZone object
  timezone = clinicLocation?.timeZone?.offset
  if (timezone) {
    // Decode if URL encoded
    if (typeof timezone === 'string' && timezone.includes('%')) {
      try {
        timezone = decodeURIComponent(timezone)
      } catch {
        timezone = '+05:30'
      }
    }
    return timezone
  }

  // Fallback to IST
  return '+05:30'
}

/**
 * Format a time slot in clinic timezone
 * @param {string|dayjs.Dayjs} startTime - Start time (UTC ISO string or dayjs object)
 * @param {string|dayjs.Dayjs} endTime - End time (UTC ISO string or dayjs object, optional)
 * @param {Object} clinicLocation - Clinic location object for timezone
 * @param {number} durationInMins - Duration in minutes (used if endTime not provided)
 * @returns {Object} Object with formatted time strings and timezone abbreviation
 */
export function formatTimeSlotInClinicTimezone(startTime, endTime = null, clinicLocation = null, durationInMins = 30) {
  // Get clinic timezone with fallback to IST
  const timezone = getClinicTimezone(clinicLocation)

  // Parse start time (assume UTC if it's a string, or if it's a dayjs object assume it's UTC)
  let start = dayjs.isDayjs(startTime) 
    ? (startTime.isUTC() ? startTime : startTime.utc()) // Ensure UTC
    : dayjs.utc(startTime)
  
  // Parse end time
  let end = endTime 
    ? (dayjs.isDayjs(endTime) 
        ? (endTime.isUTC() ? endTime : endTime.utc()) // Ensure UTC
        : dayjs.utc(endTime))
    : start.add(durationInMins, 'minutes')

  // Convert to clinic timezone (utcOffset converts from UTC to the specified offset)
  const startInTz = start.utcOffset(timezone)
  const endInTz = end.utcOffset(timezone)

  // Format times
  const startFormatted = startInTz.format('h:mm A')
  const endFormatted = endInTz.format('h:mm A')
  const label = `${startFormatted} - ${endFormatted}`

  // Get timezone abbreviation
  let tzAbbr = 'IST' // Default to IST
  try {
    if (timezone === '+05:30' || timezone === 'Asia/Kolkata' || timezone === 'Asia/Calcutta') {
      tzAbbr = 'IST'
    } else if (timezone === '+05:00') {
      tzAbbr = 'PKT'
    } else if (timezone === '+08:00') {
      tzAbbr = 'CST'
    } else if (timezone === '+00:00' || timezone === 'UTC') {
      tzAbbr = 'UTC'
    } else {
      // Try to extract offset and format as GMT+X
      const offsetMatch = timezone.match(/^([+-])(\d{2}):(\d{2})$/)
      if (offsetMatch) {
        const sign = offsetMatch[1]
        const hours = parseInt(offsetMatch[2], 10)
        const minutes = parseInt(offsetMatch[3], 10)
        if (minutes === 0) {
          tzAbbr = `GMT${sign}${hours}`
        } else {
          tzAbbr = `GMT${sign}${hours}:${minutes}`
        }
      } else {
        tzAbbr = timezone
      }
    }
  } catch {
    tzAbbr = 'IST'
  }

  return {
    startFormatted,
    endFormatted,
    label,
    labelWithTz: `${label} ${tzAbbr}`,
    tzAbbr,
    startInTz,
    endInTz,
  }
}
